import React, { useState, useRef, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { Camera, Save, User, Mail, Phone, Briefcase, PenLine, Loader2, Building2 } from "lucide-react";
import { api, fileUrl } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useCompany } from "../context/CompanyContext";
import { Avatar } from "../components/Avatar";
import { buildSignatureHtml } from "../lib/signature";
import { NotificationSettings } from "../components/NotificationSettings";

export default function ProfilePage() {
  const { user, setUser } = useAuth();
  const { selectedId } = useCompany();
  const fileRef = useRef();
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    name: user?.name || "",
    title: user?.title || "",
    phone: user?.phone || "",
    signature: user?.signature || "",
    avatar_url: user?.avatar_url || "",
  });

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const [memberships, setMemberships] = useState([]);
  const [sigCompanyId, setSigCompanyId] = useState("");
  const loadMemberships = useCallback(async () => {
    try { const { data } = await api.get("/my/memberships"); setMemberships(data); return data; } catch { setMemberships([]); return []; }
  }, []);
  useEffect(() => {
    loadMemberships().then((data) => {
      if (data.length) {
        const pref = data.find((m) => m.company_id === selectedId);
        setSigCompanyId((cur) => cur || (pref ? pref.company_id : data[0].company_id));
      }
    });
  }, [loadMemberships, selectedId]);
  const activeMembership = memberships.find((m) => m.company_id === sigCompanyId);

  const uploadAvatar = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd);
      const url = fileUrl(data.id);
      set("avatar_url", url);
      const { data: updated } = await api.patch("/profile", { avatar_url: url });
      setUser(updated);
      toast.success("Photo mise à jour");
    } catch {
      toast.error("Échec de l'envoi de la photo");
    } finally {
      setUploading(false);
    }
  };

  const save = async () => {
    if (!form.name.trim()) { toast.error("Le nom est requis"); return; }
    setSaving(true);
    try {
      const { data } = await api.patch("/profile", {
        name: form.name.trim(),
        title: form.title,
        phone: form.phone,
        signature: form.signature,
      });
      setUser(data);
      toast.success("Profil enregistré");
    } catch {
      toast.error("Échec de l'enregistrement");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="h-full overflow-y-auto bg-background" data-testid="profile-page">
      <div className="max-w-3xl mx-auto px-4 lg:px-8 py-8 lg:py-12">
        <div className="flex items-center gap-3 mb-8">
          <div className="w-11 h-11 rounded-xl bg-klein/15 border border-klein/30 flex items-center justify-center text-klein"><User size={22} /></div>
          <div>
            <h1 className="font-cabinet font-bold text-3xl tracking-tight">Mon profil</h1>
            <p className="text-sm text-zinc-500">Gérez vos informations personnelles et votre signature</p>
          </div>
        </div>

        {/* Avatar + identity card */}
        <div className="bg-card border border-white/10 rounded-2xl p-6 mb-5">
          <div className="flex flex-col sm:flex-row items-center gap-5">
            <div className="relative group/avatar">
              <Avatar name={form.name} color={user?.avatar_color} size={96} src={form.avatar_url} className="ring-2 ring-white/10" />
              <button
                data-testid="profile-avatar-upload"
                onClick={() => fileRef.current.click()}
                disabled={uploading}
                className="absolute -bottom-1 -right-1 w-9 h-9 rounded-full bg-klein text-zinc-950 flex items-center justify-center shadow-lg hover:scale-105 transition-transform"
                title="Changer la photo"
              >
                {uploading ? <Loader2 size={16} className="animate-spin" /> : <Camera size={16} />}
              </button>
              <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={uploadAvatar} data-testid="profile-avatar-input" />
            </div>
            <div className="text-center sm:text-left">
              <div className="font-cabinet font-bold text-2xl">{form.name || "—"}</div>
              {form.title && <div className="text-sm text-klein">{form.title}</div>}
              <div className="flex items-center justify-center sm:justify-start gap-1.5 text-sm text-zinc-400 mt-1"><Mail size={13} /> {user?.email}</div>
            </div>
          </div>
        </div>

        {/* Editable fields */}
        <div className="bg-card border border-white/10 rounded-2xl p-6 space-y-5">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="font-mono-label text-[11px] uppercase tracking-widest text-zinc-400 flex items-center gap-1.5 mb-1.5"><User size={12} /> Nom complet</label>
              <input data-testid="profile-name" value={form.name} onChange={(e) => set("name", e.target.value)} className="w-full bg-background border border-white/10 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-klein/20 focus:border-klein" />
            </div>
            <div>
              <label className="font-mono-label text-[11px] uppercase tracking-widest text-zinc-400 flex items-center gap-1.5 mb-1.5"><Briefcase size={12} /> Fonction / titre</label>
              <input data-testid="profile-title" value={form.title} onChange={(e) => set("title", e.target.value)} placeholder="ex: Responsable projet" className="w-full bg-background border border-white/10 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-klein/20 focus:border-klein" />
            </div>
            <div>
              <label className="font-mono-label text-[11px] uppercase tracking-widest text-zinc-400 flex items-center gap-1.5 mb-1.5"><Phone size={12} /> Téléphone</label>
              <input data-testid="profile-phone" value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder="+33 6 12 34 56 78" className="w-full bg-background border border-white/10 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-klein/20 focus:border-klein" />
            </div>
            <div>
              <label className="font-mono-label text-[11px] uppercase tracking-widest text-zinc-400 flex items-center gap-1.5 mb-1.5"><Mail size={12} /> Email (non modifiable)</label>
              <input value={user?.email || ""} disabled className="w-full bg-background/50 border border-white/10 rounded-lg px-3 py-2.5 text-sm text-zinc-500 cursor-not-allowed" />
            </div>
          </div>

          <div>
            <label className="font-mono-label text-[11px] uppercase tracking-widest text-zinc-400 flex items-center gap-1.5 mb-1.5"><PenLine size={12} /> Signature email (texte)</label>
            <textarea data-testid="profile-signature" rows={3} value={form.signature} onChange={(e) => set("signature", e.target.value)} placeholder="Cordialement,&#10;Votre nom" className="w-full bg-background border border-white/10 rounded-lg px-3 py-2.5 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-klein/20 focus:border-klein" />
            <p className="text-[11px] text-zinc-500 mt-1.5">Astuce : la signature riche avec logo/avatar est générée automatiquement depuis ces infos dans la fenêtre de rédaction.</p>
          </div>

          <div className="flex justify-end pt-1">
            <button data-testid="profile-save" onClick={save} disabled={saving} className="flex items-center gap-2 px-5 py-2.5 rounded-lg bg-klein text-zinc-950 text-sm font-semibold hover:brightness-110 transition-all disabled:opacity-50">
              {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />} Enregistrer
            </button>
          </div>
        </div>

        {/* Per-company signatures */}
        <div className="mt-8" data-testid="company-signatures-section">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-1">
            <div className="flex items-center gap-2">
              <Building2 size={18} className="text-klein" />
              <h2 className="font-cabinet font-bold text-xl tracking-tight">Signature par entreprise</h2>
            </div>
            {memberships.length > 0 && (
              <div className="relative">
                <select data-testid="sig-company-select" value={sigCompanyId} onChange={(e) => setSigCompanyId(e.target.value)} className="appearance-none bg-card border border-white/10 rounded-lg pl-3 pr-9 py-2 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-klein/20 focus:border-klein cursor-pointer">
                  {memberships.map((m) => <option key={m.company_id} value={m.company_id}>{m.company_name}</option>)}
                </select>
                <Building2 size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 pointer-events-none" />
              </div>
            )}
          </div>
          <p className="text-sm text-zinc-500 mb-4">Choisissez une entreprise pour définir une fonction, un email et un téléphone propres. Cette signature s'applique automatiquement quand vous écrivez depuis cette entreprise.</p>
          {memberships.length === 0 ? (
            <div className="bg-card border border-white/10 rounded-2xl p-8 text-center text-sm text-zinc-400">Vous n'êtes membre d'aucune entreprise pour le moment.</div>
          ) : activeMembership ? (
            <CompanySignatureCard key={activeMembership.company_id} m={activeMembership} user={user} onSaved={loadMemberships} />
          ) : null}
        </div>

        <NotificationSettings />
      </div>
    </div>
  );
}

function CompanySignatureCard({ m, user, onSaved }) {
  const [sig, setSig] = useState({ sig_title: m.sig_title || "", sig_email: m.sig_email || "", sig_phone: m.sig_phone || "" });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/companies/${m.company_id}/my-signature`, sig);
      toast.success(`Signature mise à jour — ${m.company_name}`);
      onSaved();
    } catch { toast.error("Échec de l'enregistrement"); } finally { setSaving(false); }
  };

  const previewHtml = buildSignatureHtml(user, {
    name: m.company_name, logo_url: m.logo_url,
    sig_title: sig.sig_title, sig_email: sig.sig_email, sig_phone: sig.sig_phone,
  });

  return (
    <div className="bg-card border border-white/10 rounded-2xl p-5" data-testid={`company-sig-${m.company_id}`}>
      <div className="flex items-center gap-2.5 mb-4">
        {m.logo_url ? <img src={m.logo_url} alt="" className="w-8 h-8 rounded-full object-cover" /> : <div className="w-8 h-8 rounded-full bg-klein/15 flex items-center justify-center text-klein text-xs font-bold">{(m.company_name || "?").slice(0, 1)}</div>}
        <div className="font-cabinet font-semibold">{m.company_name}</div>
        <span className="ml-auto font-mono-label text-[10px] uppercase tracking-widest text-zinc-500">{m.role}</span>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div>
          <label className="font-mono-label text-[11px] uppercase tracking-widest text-zinc-400 flex items-center gap-1.5 mb-1.5"><Briefcase size={11} /> Fonction</label>
          <input data-testid={`sig-title-${m.company_id}`} value={sig.sig_title} onChange={(e) => setSig({ ...sig, sig_title: e.target.value })} placeholder="ex: Directeur artistique" className="w-full bg-background border border-white/10 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-klein/20 focus:border-klein" />
        </div>
        <div>
          <label className="font-mono-label text-[11px] uppercase tracking-widest text-zinc-400 flex items-center gap-1.5 mb-1.5"><Mail size={11} /> Email</label>
          <input data-testid={`sig-email-${m.company_id}`} value={sig.sig_email} onChange={(e) => setSig({ ...sig, sig_email: e.target.value })} placeholder={user?.email} className="w-full bg-background border border-white/10 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-klein/20 focus:border-klein" />
        </div>
        <div>
          <label className="font-mono-label text-[11px] uppercase tracking-widest text-zinc-400 flex items-center gap-1.5 mb-1.5"><Phone size={11} /> Téléphone</label>
          <input data-testid={`sig-phone-${m.company_id}`} value={sig.sig_phone} onChange={(e) => setSig({ ...sig, sig_phone: e.target.value })} placeholder="+33 …" className="w-full bg-background border border-white/10 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-klein/20 focus:border-klein" />
        </div>
      </div>
      <div className="mt-4 rounded-lg border border-white/10 bg-background p-4 overflow-x-auto">
        <p className="font-mono-label text-[10px] uppercase tracking-widest text-zinc-500 mb-2">Aperçu de la signature</p>
        <div className="rounded-md p-4" style={{ background: "#0d0d12" }} dangerouslySetInnerHTML={{ __html: previewHtml }} />
      </div>
      <div className="flex justify-end mt-3">
        <button data-testid={`sig-save-${m.company_id}`} onClick={save} disabled={saving} className="flex items-center gap-2 px-4 py-2 rounded-lg bg-klein text-zinc-950 text-sm font-semibold hover:brightness-110 transition-all disabled:opacity-50">
          {saving ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />} Enregistrer
        </button>
      </div>
    </div>
  );
}
